import { useCallback, useEffect, useRef } from 'react';
import {
  ContextCallbacks,
  RawInput,
  inputService,
} from '../services/InputService';
import { audioService } from '../services/AudioService';
import { initKeyboardLayout } from '../services/KeyboardLayoutService';
import {
  GameplayContext,
  DungeonAction,
  InterfaceAction,
} from '../data/controls.config';

type AnyAction = InterfaceAction | DungeonAction;

/**
 * Call ONCE in a root component (e.g. App or Game).
 *
 * Audio is unlocked and preloaded by the boot LoadingScreen on the first user
 * gesture (browsers block `decodeAudioData` before one).
 */
export function useInputServiceLifecycle(): void {
  useEffect(() => {
    inputService.start();
    void initKeyboardLayout();

    return () => {
      inputService.stop();
    };
  }, []);
}

/** Returns a stable ref object for querying input each frame (no re-render). */
export function useInputRef() {
  const ref = useRef({
    isActive: (ctx: GameplayContext, action: AnyAction) =>
      inputService.isActionActive(ctx, action),
    isJustPressed: (ctx: GameplayContext, action: AnyAction) =>
      inputService.isActionJustPressed(ctx, action),
    isJustReleased: (ctx: GameplayContext, action: AnyAction) =>
      inputService.isActionJustReleased(ctx, action),
    isKeyDown: (code: string) => inputService.isKeyDown(code),
    getAxis: (axisIndex: number) => inputService.getAxis(axisIndex),
    endFrame: () => inputService.endFrame(),
  });
  return ref;
}

type ContextActions<C extends GameplayContext> = C extends 'interface'
  ? InterfaceAction
  : DungeonAction;

// A handler may return `false` to signal a denied action — the central
// interface-sound dispatch then plays the error cue instead of the confirm one.
type Handlers<A extends InterfaceAction | DungeonAction> = Partial<
  Record<A, () => void | boolean>
>;

const CURSOR_ACTIONS: ReadonlySet<InterfaceAction> = new Set([
  'UP',
  'DOWN',
  'LEFT',
  'RIGHT',
  'TAB_NEXT',
  'TAB_PREV',
]);
const BACK_ACTIONS: ReadonlySet<InterfaceAction> = new Set([
  'CANCEL',
  'UNPAUSE',
]);
const CONFIRM_ACTIONS: ReadonlySet<InterfaceAction> = new Set([
  'CONFIRM',
  'SECONDARY',
  'TERTIARY',
  'QUATERNARY',
  'DETAILS',
]);

// Single funnel for menu/interface sounds: every interface screen routes its
// input through here, so wiring the cue once covers them all. A handler that
// returns `false` (denied) swaps the confirm cue for the error one.
function playInterfaceActionSfx(
  action: InterfaceAction,
  result: void | boolean,
): void {
  if (CURSOR_ACTIONS.has(action)) {
    void audioService.playSfx('ui-cursor');
  } else if (BACK_ACTIONS.has(action)) {
    void audioService.playSfx('ui-back');
  } else if (CONFIRM_ACTIONS.has(action)) {
    void audioService.playSfx(result === false ? 'ui-error' : 'ui-confirm');
  }
}

interface UseInputContextOptions<C extends GameplayContext> {
  handlers?: Handlers<ContextActions<C>>;
  repeatable?: ReadonlyArray<ContextActions<C>>;
  onSuspended?: () => void;
  disabled?: boolean;
}

export function useInputContext<C extends GameplayContext>(
  context: C,
  options: UseInputContextOptions<C> = {},
): void {
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const entryIdRef = useRef<number | null>(null);

  useEffect(() => {
    const callbacks: ContextCallbacks = {
      onAction: (action) => {
        const handlers = optionsRef.current.handlers as
          | Handlers<InterfaceAction | DungeonAction>
          | undefined;
        const handler = handlers?.[action];
        if (!handler) {
          return;
        }
        const result = handler();
        if (context === 'interface') {
          playInterfaceActionSfx(action as InterfaceAction, result);
        }
      },
      onSuspended: () => {
        optionsRef.current.onSuspended?.();
      },
      repeatable: optionsRef.current.repeatable as
        | ReadonlyArray<InterfaceAction | DungeonAction>
        | undefined,
    };
    const id = inputService.pushContext(
      context,
      callbacks,
      optionsRef.current.disabled ?? false,
    );
    entryIdRef.current = id;
    return () => {
      inputService.popContext(id);
      entryIdRef.current = null;
    };
  }, [context]);

  useEffect(() => {
    const id = entryIdRef.current;
    if (id !== null) {
      inputService.setEntryDisabled(id, options.disabled ?? false);
    }
  }, [options.disabled]);
}

export function useInputCapture(): (
  onCapture: (raw: RawInput) => void,
) => void {
  return useCallback((onCapture) => {
    inputService.pushContext('key-capture', { onCapture });
  }, []);
}
